import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | 'contact' | null;
const modalContent: Record<Exclude<ModalType, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  privacy: {
    title: 'Privacy Policy',
    body: <div className="space-y-4 text-charcoal/80 font-light" data-id="element-45">
        <p data-id="element-46">
          <strong className="text-charcoal font-medium" data-id="element-47">Last updated:</strong>{' '}
          April 2026
        </p>
        <p data-id="element-48">
          Your privacy matters to us. This policy outlines how The Sunday Reset
          collects, uses, and protects your personal information.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-49">
          Information We Collect
        </h3>
        <p data-id="element-50">
          When you purchase the Sunday Reset Rescue Kit, we collect your name,
          email address, and payment information. Payment processing is handled
          securely by our third-party payment provider — we never store your
          credit card details.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-51">
          How We Use Your Information
        </h3>
        <p data-id="element-52">
          We use your information solely to deliver your digital product, send
          purchase confirmations, and provide customer support. We do not sell,
          rent, or share your personal data with third parties for marketing
          purposes.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-53">Cookies</h3>
        <p data-id="element-54">
          Our website may use minimal cookies for analytics purposes to improve
          your experience. You can disable cookies in your browser settings at
          any time.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-55">Your Rights</h3>
        <p data-id="element-56">
          You have the right to access, correct, or delete your personal data at
          any time. Simply reach out to us via the contact information below.
        </p>
      </div>
  },
  terms: {
    title: 'Terms of Service',
    body: <div className="space-y-4 text-charcoal/80 font-light" data-id="element-57">
        <p data-id="element-58">
          <strong className="text-charcoal font-medium" data-id="element-59">Last updated:</strong>{' '}
          April 2026
        </p>
        <p data-id="element-60">
          By purchasing and using the Sunday Reset Rescue Kit, you agree to the
          following terms.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-61">Digital Product</h3>
        <p data-id="element-62">
          The Sunday Reset Rescue Kit is a digital product delivered via instant
          download. Due to the nature of digital goods, all sales are final. No
          refunds will be issued once the product has been delivered.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-63">
          Personal Use Only
        </h3>
        <p data-id="element-64">
          This product is licensed for personal use only. You may not
          redistribute, resell, or share the materials without written
          permission from The Sunday Reset.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-65">Disclaimer</h3>
        <p data-id="element-66">
          The Sunday Reset Rescue Kit is designed for educational and
          informational purposes. It is not a substitute for professional
          medical, psychological, or therapeutic advice. If you are experiencing
          severe anxiety or mental health concerns, please consult a licensed
          professional.
        </p>
        <h3 className="font-serif text-forest text-lg mt-6" data-id="element-67">
          Intellectual Property
        </h3>
        <p data-id="element-68">
          All content, templates, and materials within the Rescue Kit are the
          intellectual property of The Sunday Reset and are protected by
          copyright law.
        </p>
      </div>
  },
  contact: {
    title: 'Contact Us',
    body: <div className="space-y-6 text-charcoal/80 font-light" data-id="element-69">
        <p data-id="element-70">
          We'd love to hear from you. Whether you have a question about the
          Rescue Kit, need support, or just want to say hi — reach out anytime.
        </p>
        <div className="bg-cream rounded-2xl p-6 space-y-4" data-id="element-71">
          <div data-id="element-72">
            <p className="text-sm text-charcoal/50 uppercase tracking-wider mb-1" data-id="element-73">
              Email
            </p>
            <p className="text-forest font-medium" data-id="element-74">hello@thesundayreset.com</p>
          </div>
          <div data-id="element-75">
            <p className="text-sm text-charcoal/50 uppercase tracking-wider mb-1" data-id="element-76">
              Response Time
            </p>
            <p className="text-charcoal" data-id="element-77">
              We typically respond within 24–48 hours.
            </p>
          </div>
          <div data-id="element-78">
            <p className="text-sm text-charcoal/50 uppercase tracking-wider mb-1" data-id="element-79">
              Follow Us
            </p>
            <p className="text-charcoal" data-id="element-80">@thesundayreset on Instagram</p>
          </div>
        </div>
        <p className="text-sm text-charcoal/60" data-id="element-81">
          For urgent matters, please include "URGENT" in your email subject
          line.
        </p>
      </div>
  }
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-6 text-center bg-[#3A3A3A]" data-id="element-82">
        <div className="max-w-4xl mx-auto" data-id="element-83">
          <p className="font-serif text-xl mb-6 text-[#F5F0E8]" data-id="element-84">
            "Breathe. Tomorrow is handled."
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-6" data-id="element-85">
            <button onClick={() => setActiveModal('privacy')} className="text-sm font-light hover:text-cream transition-colors text-[#F5F0E8]" data-id="element-86">
              Privacy Policy
            </button>
            <span className="text-[#F5F0E8]" data-id="element-87">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm font-light hover:text-cream transition-colors text-[#F5F0E8]" data-id="element-88">
              Terms of Service
            </button>
          </div>

          <p className="text-sm font-light text-[#F5F0E8]" data-id="element-89">
            © {new Date().getFullYear()} The Sunday Reset. All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence className="text-[#F5F0E8]" data-id="element-90">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm text-[#F5F0E8]" onClick={() => setActiveModal(null)} data-id="element-91">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-92">
              <div className="sticky top-0 bg-white rounded-t-3xl border-b border-sage/10 px-8 py-5 flex items-center justify-between z-10" data-id="element-93">
                <h3 className="text-2xl font-serif text-forest" data-id="element-94">
                  {modalContent[activeModal].title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-cream transition-colors" data-id="element-95">
                  <XIcon className="w-5 h-5 text-charcoal/60" data-id="element-96" />
                </button>
              </div>
              <div className="px-8 py-6" data-id="element-97">{modalContent[activeModal].body}</div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}